import React from 'react';
import useMountedGuard from 'Hooks/useMountedGuard';
import PanicError from 'Errors/Panic';

const FETCH_STARTING = 'fetch-starting';
const FETCH_SUCCEEDED = 'fetch-succeeded';
const FETCH_FAILED = 'fetch-failed';
const SET_COLLECTION = 'set-collection';

const init = () => {
  return {
    isFetching: true,
    fetchError: null,
    collection: null,
  };
};

function reducer(state, action) {
  switch (action.type) {
    case FETCH_STARTING: {
      return {
        ...state,
        isFetching: true,
        fetchError: null,
      };
    }
    case FETCH_SUCCEEDED: {
      return {
        ...state,
        isFetching: false,
        collection: action.collection,
      };
    }
    case FETCH_FAILED: {
      return {
        ...state,
        isFetching: false,
        fetchError: action.fetchError,
      };
    }
    case SET_COLLECTION: {
      return {
        ...state,
        collection: action.collection,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

/**
 * useLink
 *
 * Provides an interface for fetching data via facet rest.
 */
const useLink = (link, options = {}) => {
  const {
    onError,
  } = options;

  const [state, _dispatch] = React.useReducer(reducer, null, init);
  const { doWithMountedGuard } = useMountedGuard();

  const dispatch = doWithMountedGuard(_dispatch);

  async function fetchData(_link) {
    if (!_link) {
      throw new PanicError('missing-link', 'Developer Configuration Error: A link is required to fetch data.');
    }

    try {
      dispatch({ type: FETCH_STARTING });

      const resp = await _link.get();

      dispatch({ type: FETCH_SUCCEEDED, collection: resp });
    } catch (err) {
      dispatch({ type: FETCH_FAILED, fetchError: err });
      if (typeof onError === 'function') {
        onError(err);
      }
    }
  }

  // If the link changes, fetch the new data
  React.useEffect(() => {
    fetchData(link);
  }, [link]);

  const refreshCollection = React.useCallback(() => {
    if (!state.collection) {
      return undefined;
    }

    return fetchData(state.collection.link('self'));
  }, [state]);

  const setCollection = (coll) => {
    dispatch({ type: SET_COLLECTION, collection: coll });
  };

  return {
    isInitializing: !state.collection && state.isFetching, // If no collection and fetching, then this is initializing
    isInitialized: !!state.collection,
    loading: state.isFetching,
    loadingError: state.fetchError,
    collection: state.collection,
    refreshCollection,
    setCollection,
  };
};

export default useLink;
